import Modal from '@/common/components/Modal'
import { ConsumptionData } from '../types/consumption.type'
import { useState } from 'react'
import usePrint from '@/common/hooks/usePrint'
import { useTransactionIdentifierWithReceipt } from '@/common/hooks/queries/useTransactionIdentifierWithReceipt'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { PrinterIcon } from 'lucide-react'
import Checkbox from '@/common/components/Checkbox'
import PrintContext from '@/common/components/PrintContext'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { isMobile } from 'react-device-detect'

interface ModalSuccessProps {
  data: ConsumptionData
  setData: React.Dispatch<React.SetStateAction<boolean>>
  clearForm: () => void
}

const ModalSuccess = ({ data, setData, clearForm }: ModalSuccessProps) => {
  const { hasUserPermission } = useUserPermissions();

  const hasGetTransactionIdentifierPermission = hasUserPermission('Establishment::Billing::TransactionIdentifiers::ShowWithReceipt');

  const [doNotPrintReceipt, setDoNotPrintReceipt] = useState(isMobile)

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { print, contextProps } = usePrint({
    onAfterPrint: () => {
      setData(false);
      clearForm();
    },
  });

  const {
    mutate: getTransactionIdentifier,
    isPending: isGetTransactionIdentifierPending,
  } = useTransactionIdentifierWithReceipt({
    transactionIdentifierId: data?.protocol_number,
    onSuccess: (data) => {
      const receipt = data?.eventable?.receipt?.text;

      if (receipt) {
        print({ content: receipt });
      } else {
        setMessages(['Ocorreu um erro ao buscar as informações do comprovante'])
      }
    },
    onError: (error) => {
      setMessages(error.response?.data.errors.base || ['Ocorreu um erro ao buscar as informações do comprovante'])
    },
  })

  const closeModalAndClearForm = () => {
    if (!hasGetTransactionIdentifierPermission || doNotPrintReceipt) {
      setData(false);
      clearForm();
    } else {
      getTransactionIdentifier();
    }
    setDoNotPrintReceipt(false);
  };

  return (
    <>
      <Modal
        isOpen={!!data}
        onConfirm={closeModalAndClearForm}
        Trigger={<></>}
        showClose={false}
        footerIcon={
          (hasGetTransactionIdentifierPermission && !doNotPrintReceipt && !isMobile)
            ? <PrinterIcon strokeWidth={1.5} size={16} className='-mr-2' />
            : undefined
        }
        footer={
          hasGetTransactionIdentifierPermission && !isMobile ?
            !doNotPrintReceipt
              ? 'Imprimir comprovante'
              : 'Finalizar transação sem comprovante'
            : 'Entendido!'
        }
        confirmButtonIsLoading={isGetTransactionIdentifierPending}
        disabled={isGetTransactionIdentifierPending}
        title="Transação bem sucedida!"
        body={(
          <div className="flex flex-col gap-8 py-2">
            <div className="flex flex-col gap-2">
              <div>
                <p className="text-[14px] font-light">ID da requisição</p>
                <p className="text-sm sm:text-[16px] text-muted">{data.id}</p>
              </div>
              <div>
                <p className="text-[14px] font-light">Número de protocolo</p>
                <p className="text-sm sm:text-[16px] text-muted">{data.protocol_number}</p>
              </div>
            </div>

            {!isMobile && hasGetTransactionIdentifierPermission && (
              <div>
                <Checkbox
                  name="do_not_print_receipt"
                  checked={doNotPrintReceipt}
                  onChange={(e) => setDoNotPrintReceipt(e.currentTarget.checked)}
                  label='Não desejo imprimir compovante'
                  labelClassName='italic text-white-800 select-none'
                />
              </div>
            )}
          </div>
        )}
      />

      <PrintContext {...contextProps} className="text-black-900 px-1" />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
          onClose={() => {
            setData(false);
            clearForm();
          }}
        />
      )}
    </>
  )
}

export default ModalSuccess
